import * as React from 'react';
import { Box, Card, CardContent, Divider, Grid, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';

const textWidth = 160;

export type TeamRSCI = {
  awareness: number;
  sensemaking: number;
  regulation: number;
  plasticity: number;
  total: number;
};

export type TeamNorme = {
  conoscersi: number;
  costruire: number;
  discutere: number;
  proattivita: number;
  contesto: number;
  connettersi: number;
};

export type TeamTableItem = {
  team_id: string;
  fundamentals: number;
  social_capital: number;
  activation: number;
  reciprocity: number;
  rsci: TeamRSCI;
  norme: TeamNorme;
  flag: string[];
};

export type TeamOfTeamsAnalysisData = {
  OSCAR: number;

  IntraTeamScore: number;
  ActivationScore: number;
  ReciprocityScore: number;
  InterTeamHomogeneity: number;

  AVGRSCI: number;

  AVGAwareness: number;
  SDAwareness: number;
  AVGSensemaking: number;
  SDSensemaking: number;
  AVGRegulation: number;
  SDRegulation: number;
  AVGPlasticity: number;
  SDPlasticity: number;

  AVGFondamentali: number;
  SDFondamentali: number;

  AVGConoscersi: number;
  SDConoscersi: number;
  AVGCostruire: number;
  SDCostruire: number;
  AVGDiscutere: number;
  SDDiscutere: number;
  ['AVGProattività']: number;
  ['SDProattività']: number;
  AVGContesto: number;
  SDContesto: number;
  AVGConnettersi: number;
  SDConnettersi: number;
  RSCI_range_org: {
    awareness: number;
    sensemaking: number;
    regulation: number;
    plasticity: number;
    total: number;
  };
  teamTable: TeamTableItem[];
};

export type TeamOfTeamsAnalysisProps = {
  data: TeamOfTeamsAnalysisData;
  /**
   * How many decimals to show (default: 2)
   */
  decimals?: number;
};

function TeamCard({ team, decimals }: { team: TeamTableItem; decimals: number }) {
  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle2" fontWeight={700}>
          {team.team_id}
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          <Box flex={1} minWidth={260}>
            <MetricRow label="Fundamentals" value={team.fundamentals} decimals={decimals} />
            <MetricRow label="Social Capital" value={team.social_capital} decimals={decimals} />
            <MetricRow label="Activation" value={team.activation} decimals={decimals} />
            <MetricRow label="Reciprocity" value={team.reciprocity} decimals={decimals} />
          </Box>
          <Box flex={1} minWidth={260}>
            <Section
              title="RSCI"
              sectionScore={<MetricValue value={team.rsci.total} decimals={decimals} />}
            >
              <MetricRow label="Awareness" value={team.rsci.awareness} decimals={decimals} />
              <MetricRow label="Sensemaking" value={team.rsci.sensemaking} decimals={decimals} />
              <MetricRow label="Regulation" value={team.rsci.regulation} decimals={decimals} />
              <MetricRow label="Plasticity" value={team.rsci.plasticity} decimals={decimals} />
            </Section>
          </Box>
          <Box flex={1} minWidth={260}>
            <Section title="Norme">
              <MetricRow label="Conoscersi" value={team.norme.conoscersi} decimals={decimals} />
              <MetricRow label="Costruire" value={team.norme.costruire} decimals={decimals} />
              <MetricRow label="Discutere" value={team.norme.discutere} decimals={decimals} />
              <MetricRow label="Proattività" value={team.norme.proattivita} decimals={decimals} />
              <MetricRow label="Contesto" value={team.norme.contesto} decimals={decimals} />
              <MetricRow label="Connettersi" value={team.norme.connettersi} decimals={decimals} />
            </Section>
          </Box>
        </Stack>
      </Stack>
    </Card>
  );
}

function formatNumber(n: number, decimals: number) {
  if (Number.isNaN(n) || !Number.isFinite(n)) return '—';
  return n.toFixed(decimals);
}

function MetricRow(props: {
  label: string;
  value: number;
  decimals: number;
  secondary?: React.ReactNode;
  tertiary?: React.ReactNode;
}) {
  const { label, value, decimals, secondary, tertiary } = props;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 1,
        py: 0.75,
      }}
    >
      <Typography variant="body2" color="text.secondary" width={textWidth}>
        {label}
      </Typography>
      <MetricValue secondary={secondary} value={value} decimals={decimals} tertiary={tertiary} />
    </Box>
  );
}

function MetricValue(props: {
  secondary?: React.ReactNode;
  value: number;
  decimals: number;
  tertiary?: React.ReactNode;
}) {
  const { value, decimals, secondary, tertiary } = props;
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
      <Typography variant="body1" fontWeight={600}>
        {formatNumber(value, decimals)}
      </Typography>
      {tertiary}
      {secondary}
    </Box>
  );
}

function AvgSdRow(props: {
  label: string;
  avg: number;
  sd: number;
  decimals: number;
  range?: number;
}) {
  const { label, avg, sd, decimals, range } = props;
  return (
    <MetricRow
      label={label}
      value={avg}
      decimals={decimals}
      secondary={
        <Typography variant="caption" color="text.secondary">
          SD {formatNumber(sd, decimals)}
        </Typography>
      }
      tertiary={
        range && (
          <Typography variant="caption" color="text.secondary">
            RNG {formatNumber(range, decimals)}
          </Typography>
        )
      }
    />
  );
}

function Section(props: {
  title: string;
  children?: React.ReactNode;
  sectionScore?: React.ReactNode;
  range?: React.ReactNode;
}) {
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle1" fontWeight={700} width={textWidth}>
          {props.title}
        </Typography>
        {props.sectionScore}
        {props.range}
      </Stack>
      <Box>{props.children}</Box>
    </Stack>
  );
}

export default function TeamOfTeamsAnalysis({ data, decimals = 2 }: TeamOfTeamsAnalysisProps) {
  const [teamsOpen, setTeamsOpen] = React.useState(false);
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2.5}>
          <Grid container spacing={3}>
            {/* Left column */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={2.5}>
                <Section
                  title="OSCAR"
                  sectionScore={<MetricValue value={data.OSCAR} decimals={decimals} />}
                >
                  <MetricRow
                    label="IntraTeamScore"
                    value={data.IntraTeamScore}
                    decimals={decimals}
                  />
                  <MetricRow
                    label="ActivationScore"
                    value={data.ActivationScore}
                    decimals={decimals}
                  />
                  <MetricRow
                    label="ReciprocityScore"
                    value={data.ReciprocityScore}
                    decimals={decimals}
                  />
                  <MetricRow
                    label="InterTeamHomogeneity"
                    value={data.InterTeamHomogeneity}
                    decimals={decimals}
                  />
                </Section>
                <Divider />
                <Section
                  title="RSCI"
                  sectionScore={<MetricValue value={data.AVGRSCI} decimals={decimals} />}
                  range={
                    <Typography variant="caption" color="text.secondary">
                      RNG {formatNumber(data.RSCI_range_org.total, decimals)}
                    </Typography>
                  }
                >
                  <AvgSdRow
                    label="Awareness"
                    avg={data.AVGAwareness}
                    sd={data.SDAwareness}
                    decimals={decimals}
                    range={data.RSCI_range_org.awareness}
                  />
                  <AvgSdRow
                    label="Sensemaking"
                    avg={data.AVGSensemaking}
                    sd={data.SDSensemaking}
                    decimals={decimals}
                    range={data.RSCI_range_org.sensemaking}
                  />
                  <AvgSdRow
                    label="Regulation"
                    avg={data.AVGRegulation}
                    sd={data.SDRegulation}
                    decimals={decimals}
                    range={data.RSCI_range_org.regulation}
                  />
                  <AvgSdRow
                    label="Plasticity"
                    avg={data.AVGPlasticity}
                    sd={data.SDPlasticity}
                    decimals={decimals}
                    range={data.RSCI_range_org.plasticity}
                  />
                </Section>
                <Divider />
              </Stack>
            </Grid>

            {/* Right column */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={2.5}>
                <Section
                  title="Fondamentali"
                  sectionScore={
                    <MetricValue
                      value={data.AVGFondamentali}
                      decimals={decimals}
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          SD {formatNumber(data.SDFondamentali, decimals)}
                        </Typography>
                      }
                    />
                  }
                />
                <Divider />
                <Section title="Norme">
                  <AvgSdRow
                    label="Conoscersi"
                    avg={data.AVGConoscersi}
                    sd={data.SDConoscersi}
                    decimals={decimals}
                  />
                  <AvgSdRow
                    label="Costruire"
                    avg={data.AVGCostruire}
                    sd={data.SDCostruire}
                    decimals={decimals}
                  />
                  <AvgSdRow
                    label="Discutere"
                    avg={data.AVGDiscutere}
                    sd={data.SDDiscutere}
                    decimals={decimals}
                  />
                  <AvgSdRow
                    label="Proattività"
                    avg={data['AVGProattività']}
                    sd={data['SDProattività']}
                    decimals={decimals}
                  />
                  <AvgSdRow
                    label="Contesto"
                    avg={data.AVGContesto}
                    sd={data.SDContesto}
                    decimals={decimals}
                  />
                  <AvgSdRow
                    label="Connettersi"
                    avg={data.AVGConnettersi}
                    sd={data.SDConnettersi}
                    decimals={decimals}
                  />
                </Section>
              </Stack>
            </Grid>
          </Grid>
          <Divider />

          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight={700}>
                Teams ({data.teamTable.length})
              </Typography>

              <IconButton size="small" onClick={() => setTeamsOpen((o) => !o)}>
                <ExpandMoreIcon
                  sx={{
                    transform: teamsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}
                />
              </IconButton>
            </Stack>

            <Collapse in={teamsOpen} timeout="auto" unmountOnExit>
              <Stack spacing={2} mt={1}>
                {data.teamTable.map((team) => (
                  <TeamCard key={team.team_id} team={team} decimals={decimals} />
                ))}
              </Stack>
            </Collapse>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
